import React, { useEffect, useRef, useState } from 'react';
import { View, TouchableOpacity } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import { LinearGradient } from 'expo-linear-gradient';

import { Text } from '@/components/ui/text';
import { useTranslation } from '../../hooks/useTranslation';
import { ROUTES } from '../../navigation/routes';
import { useAuth } from '../../context/AuthContext';
import { useTrialContext } from '../../context/TrialContext';
import { brand } from '../../design-system/theme/tokens';

export function getTrialUrgencyColors(hoursRemaining: number) {
  if (hoursRemaining <= 6) {
    return {
      bg: ['#FEF2F2', '#FEE2E2'] as [string, string],
      border: '#FECACA',
      text: '#DC2626',
      icon: '#EF4444',
      badge: '#FEE2E2',
    };
  }
  if (hoursRemaining <= 24) {
    return {
      bg: ['#FFFBEB', '#FEF3C7'] as [string, string],
      border: '#FDE68A',
      text: '#D97706',
      icon: '#F59E0B',
      badge: '#FEF3C7',
    };
  }
  return {
    bg: ['#EFF6FF', '#DBEAFE'] as [string, string],
    border: '#BFDBFE',
    text: brand.primary,
    icon: '#3B82F6',
    badge: '#DBEAFE',
  };
}

interface TrialBannerProps {
  expiresAt: string | null;
  messagesRemaining: number;
  chatCount: number;
  kind?: 'legacy_no_card' | 'card_backed' | null;
  grandfathered?: boolean;
}

export default function TrialBanner({
  expiresAt,
  messagesRemaining,
  chatCount,
  kind,
  grandfathered = false,
}: TrialBannerProps) {
  const { t } = useTranslation();
  const router = useRouter();
  const { refreshAuth } = useAuth();
  const { refreshTrialStatus } = useTrialContext();
  const [timeLeft, setTimeLeft] = useState('');
  const [hoursRemaining, setHoursRemaining] = useState(24 * 7);
  const expiryHandled = useRef(false);

  useEffect(() => {
    if (!expiresAt) return;

    const update = () => {
      const now = new Date().getTime();
      const exp = new Date(expiresAt).getTime();
      const diff = exp - now;

      if (diff <= 0) {
        setTimeLeft(t('trial.banner.expired') || 'Expired');
        setHoursRemaining(0);
        if (!expiryHandled.current) {
          expiryHandled.current = true;
          void Promise.all([
            refreshTrialStatus(),
            refreshAuth({ forceBillingRefresh: true, forceUserRefresh: true }),
          ]);
        }
        return;
      }

      const totalHours = diff / (1000 * 60 * 60);
      setHoursRemaining(totalHours);
      const hours = Math.floor(totalHours);
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      if (hours > 24) {
        const days = Math.floor(hours / 24);
        const remHours = hours % 24;
        setTimeLeft(`${days}d ${remHours}h`);
      } else {
        setTimeLeft(`${hours}h ${minutes}m`);
      }
    };

    update();
    const interval = setInterval(update, 30000);
    const expiresInMs = expiresAt ? new Date(expiresAt).getTime() - Date.now() : -1;
    const expiryTimer = expiresInMs > 0
      ? setTimeout(update, Math.min(expiresInMs + 50, 2_147_483_647))
      : undefined;
    return () => {
      clearInterval(interval);
      if (expiryTimer) clearTimeout(expiryTimer);
    };
  }, [expiresAt, refreshAuth, refreshTrialStatus, t]);

  const handleSubscribe = () => {
    router.push(ROUTES.GATED_PLAN_SELECTION);
  };

  const colors = getTrialUrgencyColors(hoursRemaining);
  const messagesUsed = chatCount;
  const totalMessages = Math.max(messagesUsed + messagesRemaining, messagesUsed, 1);
  const dotCount = Math.min(totalMessages, 10);
  const filledDots = Math.min(
    dotCount,
    Math.round((messagesUsed / totalMessages) * dotCount),
  );

  return (
    <View
      testID="trial_status_banner"
      className="mx-4 mb-3 rounded-xl overflow-hidden"
      style={{ borderWidth: 1, borderColor: colors.border }}
    >
      <LinearGradient colors={colors.bg} start={{ x: 0, y: 0 }} end={{ x: 1, y: 0 }}>
        <View className="px-4 py-3">
          <View className="flex-row items-center justify-between">
            <View className="flex-row items-center flex-1">
              <View className="flex-1">
                <View className="flex-row items-center mb-1.5" style={{ gap: 8 }}>
                  {/* Time badge */}
                  <View className="flex-row items-center px-2 py-1 rounded-md" style={{ backgroundColor: colors.badge }}>
                    <MaterialIcons name="schedule" size={13} color={colors.icon} />
                    <Text className="ml-1 text-xs font-bold" style={{ color: colors.text }}>
                      {timeLeft}
                    </Text>
                  </View>

                  {/* Message dots */}
                  <View className="flex-row items-center" style={{ gap: 3 }}>
                    {Array.from({ length: dotCount }).map((_, i) => (
                      <View
                        key={i}
                        style={{
                          width: 7,
                          height: 7,
                          borderRadius: 3.5,
                          backgroundColor: i < filledDots ? colors.icon : `${colors.icon}30`,
                        }}
                      />
                    ))}
                    <Text className="ml-1 text-xs font-medium" style={{ color: colors.text }}>
                      {t('trial.banner.messagesOf')?.replace('{{used}}', String(chatCount)).replace('{{total}}', String(totalMessages)) || `${chatCount} of ${totalMessages} used`}
                    </Text>
                  </View>
                </View>

                {/* Label */}
                <Text className="text-xs" style={{ color: colors.text }}>
                  {grandfathered
                    ? t('trial.banner.legacyLabel')
                    : kind === 'card_backed'
                      ? t('trial.banner.cardBackedLabel')
                      : t('trial.banner.label')}
                  {messagesRemaining === 0
                    ? ` — ${t('trial.banner.noMessages') || 'no messages left'}`
                    : hoursRemaining <= 6
                      ? ` — ${t('trial.banner.expiringSoon') || 'expiring soon'}`
                      : ''}
                </Text>
              </View>
            </View>

            <TouchableOpacity
              onPress={handleSubscribe}
              className="ml-3 px-3 py-2 rounded-lg"
              style={{ backgroundColor: colors.text }}
              activeOpacity={0.85}
            >
              <Text className="text-xs font-bold text-white">
                {t('trial.banner.subscribeCTA') || 'Upgrade'}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </LinearGradient>
    </View>
  );
}
